
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Target, Plus, Eye, Calendar } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Vision {
  id: string;
  title: string;
  description: string;
  category: string;
  deadline: string;
  progress: number;
}

const DreamBoardPlus: React.FC = () => {
  const { toast } = useToast();
  const [visions, setVisions] = useState<Vision[]>([
    {
      id: '1',
      title: 'Start My Own Tech Company',
      description: 'Create a tech startup focused on sustainable solutions',
      category: 'Entrepreneurship',
      deadline: '2025-12-31',
      progress: 25
    }
  ]);
  const [newVision, setNewVision] = useState({
    title: '',
    description: '',
    category: '',
    deadline: ''
  });

  const categories = [
    'Career', 'Education', 'Entrepreneurship', 'Personal Development',
    'Health & Wellness', 'Relationships', 'Financial', 'Creative'
  ];

  const addVision = () => {
    if (!newVision.title) return;
    
    const vision: Vision = {
      id: Date.now().toString(),
      ...newVision,
      progress: 0
    };
    
    setVisions([...visions, vision]);
    setNewVision({ title: '', description: '', category: '', deadline: '' });
    toast({
      title: "Vision Added to DreamBoard!",
      description: "Your new vision has been added to your planning workspace."
    });
  };

  const updateProgress = (id: string, progress: number) => {
    setVisions(visions.map(vision => 
      vision.id === id ? { ...vision, progress } : vision
    ));
    toast({
      title: "Progress Updated!",
      description: `Vision progress updated to ${progress}%.`
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-purple-500" />
            DreamBoard+ Vision Planning Tool
          </CardTitle>
          <CardDescription>
            Create and track your long-term visions with our advanced planning workspace.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add New Vision */}
          <div className="border rounded-lg p-4 bg-purple-50">
            <h3 className="font-semibold mb-3">Add New Vision</h3>
            <div className="space-y-3">
              <div>
                <Label htmlFor="vision-title">Vision Title</Label>
                <Input
                  id="vision-title"
                  value={newVision.title}
                  onChange={(e) => setNewVision({...newVision, title: e.target.value})}
                  placeholder="e.g., Become a Software Engineer"
                />
              </div>
              <div>
                <Label htmlFor="vision-description">Vision Description</Label>
                <Textarea
                  id="vision-description"
                  value={newVision.description}
                  onChange={(e) => setNewVision({...newVision, description: e.target.value})}
                  placeholder="Describe your vision in detail"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="vision-category">Category</Label>
                  <select
                    id="vision-category"
                    className="w-full p-2 border rounded-md"
                    value={newVision.category}
                    onChange={(e) => setNewVision({...newVision, category: e.target.value})}
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="vision-deadline">Target Deadline</Label>
                  <Input
                    id="vision-deadline"
                    type="date"
                    value={newVision.deadline}
                    onChange={(e) => setNewVision({...newVision, deadline: e.target.value})}
                  />
                </div>
              </div>
              <Button onClick={addVision} className="bg-purple-500 hover:bg-purple-600">
                <Plus className="h-4 w-4 mr-2" />
                Add to DreamBoard
              </Button>
            </div>
          </div>

          {/* Visions Grid */}
          <div className="space-y-3">
            <h3 className="font-semibold">Your Vision Board</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {visions.map((vision) => (
                <div key={vision.id} className="border rounded-lg p-4 bg-gradient-to-br from-purple-50 to-pink-50">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-lg">{vision.title}</h4>
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                      {vision.category}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{vision.description}</p>
                  
                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Progress</span>
                      <span>{vision.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${vision.progress}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      {vision.deadline}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => updateProgress(vision.id, Math.min(100, vision.progress + 25))}
                      >
                        +25%
                      </Button>
                      <Button 
                        size="sm" 
                        className="bg-purple-500 hover:bg-purple-600"
                        onClick={() => {
                          toast({
                            title: "Vision Details",
                            description: "Vision board details and planning tools coming soon!"
                          });
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DreamBoardPlus;
